import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, Suspense } from 'react';
import {
  Inbox as InboxIcon,
  FileText,
  Sliders,
  BarChart3,
  Users,
  ShieldCheck,
  Megaphone,
  Settings as SettingsIcon,
  Command as CommandIcon,
  Loader2,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { wsClient, useWsEvent } from './lib/ws';
import { api } from './lib/api';
import { CommandPalette } from './components/CommandPalette';

/**
 * Hält den Browser-Tab-Titel mit der Anzahl ungelesener (offener) Konversationen aktuell
 * — wie bei Gmail „(3) …". Aktualisiert per WS-Event (gebündelt) + Intervall-Fallback,
 * damit der Zähler auch ohne aktive Inbox-Seite nicht veraltet.
 */
function useUnreadTabTitle(): number {
  const { data, refetch } = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => api.get<{ count: number }>('/api/conversations/unread-count'),
    staleTime: 20_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const count = data?.count ?? 0;
  useEffect(() => {
    document.title = count > 0 ? `(${count}) Posteingang · kalkül` : 'Posteingang · kalkül';
  }, [count]);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useWsEvent((event) => {
    if (
      event.type !== 'message.created' &&
      event.type !== 'conversation.created' &&
      event.type !== 'conversation.updated'
    ) {
      return;
    }
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      refetch();
    }, 1000);
  });
  useEffect(
    () => () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    },
    [],
  );
  return count;
}

const NAV = [
  { to: '/inbox', label: 'Posteingang', icon: InboxIcon },
  { to: '/contacts', label: 'Kontakte', icon: Users },
  { to: '/templates', label: 'Vorlagen', icon: FileText },
  { to: '/rules', label: 'Regeln', icon: Sliders },
  { to: '/broadcasts', label: 'Broadcasts', icon: Megaphone },
  { to: '/reports', label: 'Berichte', icon: BarChart3 },
  { to: '/audit', label: 'Audit-Log', icon: ShieldCheck },
  { to: '/settings', label: 'Einstellungen', icon: SettingsIcon },
];

/** `g`-Prefix-Kürzel: g i → Inbox, g c → Kontakte, usw. */
const GOTO_SHORTCUTS: Record<string, string> = {
  i: '/inbox',
  c: '/contacts',
  v: '/templates',
  b: '/broadcasts',
  r: '/reports',
  s: '/settings',
};

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
}

export function App() {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Merkt sich, ob zuletzt „g" gedrückt wurde (Chord-Prefix), mit Zeitfenster.
  const gotoArmedAt = useRef<number | null>(null);

  const unreadCount = useUnreadTabTitle();

  useEffect(() => {
    wsClient.connect();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Cmd/Ctrl+K — Command-Palette (auch in Eingabefeldern erlaubt).
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      // Modifier-Tasten oder Tippen in Feldern → keine Chord-Kürzel.
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) {
        gotoArmedAt.current = null;
        return;
      }
      const key = e.key.toLowerCase();
      const now = Date.now();
      if (gotoArmedAt.current && now - gotoArmedAt.current < 1200) {
        const target = GOTO_SHORTCUTS[key];
        gotoArmedAt.current = null;
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        return;
      }
      if (key === 'g') {
        gotoArmedAt.current = now;
      } else {
        gotoArmedAt.current = null;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <div className="grid h-screen grid-cols-[248px_1fr] overflow-hidden bg-kalkuel-surface text-kalkuel-primary">
      <aside className="flex min-h-0 flex-col bg-nav-gradient text-white">
        {/* Branding */}
        <div className="flex h-16 shrink-0 items-center gap-3 px-5">
          <BrandMark />
          <div className="min-w-0 leading-none">
            <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-kalkuel-accent">
              kalkül
            </div>
            <div className="mt-1 text-[15px] font-semibold tracking-tight text-white">Inbox</div>
          </div>
        </div>
        <div className="mx-5 h-px shrink-0 bg-white/10" />

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'group relative flex items-center gap-3 rounded-field py-2 pl-4 pr-3 text-sm transition-colors duration-150',
                  isActive
                    ? 'bg-white/10 font-medium text-white'
                    : 'font-normal text-white/65 hover:bg-white/5 hover:text-white',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  {/* Linker Akzent-Indikator */}
                  <span
                    aria-hidden="true"
                    className={[
                      'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-pill bg-kalkuel-accent transition-opacity duration-150',
                      isActive ? 'opacity-100' : 'opacity-0',
                    ].join(' ')}
                  />
                  <item.icon
                    size={17}
                    className={[
                      'shrink-0 transition-colors duration-150',
                      isActive ? 'text-kalkuel-accent' : 'text-white/60 group-hover:text-white/90',
                    ].join(' ')}
                  />
                  <span className="truncate">{item.label}</span>
                  {item.to === '/inbox' && unreadCount > 0 && (
                    <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-pill bg-kalkuel-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-kalkuel-ink">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Command-Palette-Trigger */}
        <div className="shrink-0 px-3 pb-3">
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex w-full items-center gap-2.5 rounded-field border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60 transition-colors duration-150 hover:border-white/20 hover:bg-white/10 hover:text-white"
            title="Schnellsuche (Strg/Cmd + K)"
          >
            <CommandIcon size={15} className="shrink-0 text-kalkuel-accent" />
            <span>Schnellsuche</span>
            <span className="ml-auto rounded border border-white/15 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-white/50">
              ⌘K
            </span>
          </button>
        </div>

        <footer className="shrink-0 border-t border-white/10 px-5 py-4 text-[11px] text-white/45">
          <div className="font-medium text-white/60">kalkül IT Service</div>
          <div className="mt-0.5 font-light">inbox.kalkuel.app</div>
        </footer>
      </aside>
      <main className="min-h-0 overflow-hidden bg-kalkuel-surface">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center text-kalkuel-muted">
              <Loader2 size={20} className="animate-spin" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-field bg-white/10 ring-1 ring-inset ring-white/15">
      <svg viewBox="0 0 24 24" className="h-5 w-5 text-kalkuel-accent" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M4 4v16" strokeLinecap="round" />
        <path d="M20 4L8 12l12 8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
